'use client';

import { useMemo, ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProjectRecord, ProjectsFiltersState, FilterChip } from '@/lib/projects/project-types';
import { getCountryCounts, getOrganizationCounts, getSectorCounts, getProductGroupCounts } from '@/lib/projects/project-utils';
import { IPD_PRODUCT_GROUPS } from '@/lib/reference-data/product-groups';
import { IPD_COUNTRIES, CONTINENTS, groupCountriesByContinent } from '@/lib/reference-data/countries';
import { SECTORS } from '@/lib/reference-data/sectors';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { MultiSelectPopover, MultiSelectItem, MultiSelectGroup } from '@/components/projects/MultiSelectPopover';
import { ActiveFilterChips } from '@/components/projects/ActiveFilterChips';

interface ProjectFiltersProps {
  projects: ProjectRecord[];
  value: ProjectsFiltersState;
  onChange: (next: ProjectsFiltersState) => void;
  onClear: () => void;
  chips: FilterChip[];
  onRemoveChip: (key: string) => void;
  rightSlot?: ReactNode;
}

export function ProjectFilters({
  projects,
  value,
  onChange,
  onClear,
  chips,
  onRemoveChip,
  rightSlot,
}: ProjectFiltersProps) {
  const countryGroups: MultiSelectGroup[] = useMemo(() => {
    const counts = getCountryCounts(projects);
    const grouped = groupCountriesByContinent(IPD_COUNTRIES);
    return CONTINENTS.map((continent) => ({
      label: continent,
      items: (grouped[continent] ?? [])
        .map((c) => ({
          value: c.name,
          label: c.name,
          count: counts[c.name] ?? 0,
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    }));
  }, [projects]);

  const orgItems: MultiSelectItem[] = useMemo(() => {
    const counts = getOrganizationCounts(projects);
    return Object.entries(counts)
      .map(([org, count]) => ({
        value: org,
        label: org,
        count,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [projects]);

  const sectorItems: MultiSelectItem[] = useMemo(() => {
    const counts = getSectorCounts(projects);
    return SECTORS.map((sector) => ({
      value: sector,
      label: sector,
      count: counts[sector] ?? 0,
    }));
  }, [projects]);

  const productGroupItems: MultiSelectItem[] = useMemo(() => {
    const counts = getProductGroupCounts(projects);
    return IPD_PRODUCT_GROUPS.map((pg) => ({
      value: pg.key,
      label: pg.label,
      count: counts[pg.key] || 0,
    })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [projects]);

  const cfpOptions = [
    { value: 'any', label: 'Any' },
    { value: 'hasCfp', label: 'Has CFP' },
  ];

  return (
    <div className="sticky top-[calc(var(--header-height,64px)+0px)] z-30 -mx-4 px-4 lg:-mx-6 lg:px-6 py-3 mb-4 bg-background/80 backdrop-blur-md border-b">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">CFP</span>
            <SegmentedControl
              value={value.cfpMode}
              onChange={(v) => onChange({ ...value, cfpMode: v as 'any' | 'hasCfp' })}
              options={cfpOptions}
              ariaLabel="Filter by CFP status"
            />
          </div>
          <MultiSelectPopover
            label="Country"
            placeholder="Search countries..."
            groups={countryGroups}
            selected={value.selectedCountries}
            onChange={(next) => onChange({ ...value, selectedCountries: next })}
            ariaLabel="Filter by country"
          />

          <MultiSelectPopover
            label="Organisation"
            placeholder="Search organisations..."
            items={orgItems}
            selected={value.selectedOrganizations}
            onChange={(next) => onChange({ ...value, selectedOrganizations: next })}
            ariaLabel="Filter by organisation"
          />

          <MultiSelectPopover
            label="Sector"
            placeholder="Search sectors..."
            items={sectorItems}
            selected={value.selectedSectors}
            onChange={(next) => onChange({ ...value, selectedSectors: next })}
            ariaLabel="Filter by sector"
          />

          <MultiSelectPopover
            label="Product group"
            placeholder="Search product groups..."
            items={productGroupItems}
            selected={value.selectedProductGroups}
            onChange={(next) => onChange({ ...value, selectedProductGroups: next })}
            ariaLabel="Filter by product group"
          />

          <Select
            value={value.sortMode}
            onValueChange={(v) =>
              onChange({
                ...value,
                sortMode: v as 'relevance' | 'newest' | 'endDate',
              })
            }
          >
            <SelectTrigger size="sm" className="w-32">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="endDate">End date</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {rightSlot && (
          <div className="shrink-0">{rightSlot}</div>
        )}
      </div>

      <ActiveFilterChips
        chips={chips}
        onRemove={onRemoveChip}
        onClearAll={onClear}
      />
    </div>
  );
}
